import type { Middleware } from "openapi-fetch";
import { ApiError, type ApiErrorPayload } from "../errors/ApiError";
import type { ErrorCode } from "../errors/errorCodes";

/**
 * Converts 4xx/5xx RFC 7807 responses into thrown `ApiError`.
 *
 * `openapi-fetch` v0.17 does NOT throw on 4xx/5xx by default — it returns
 * `{ data, error, response }`. We intercept in `onResponse` and throw so
 * consumer hook factories can rely on Promise rejection (and TanStack Query's
 * `error` state) rather than hand-checking `.error` on every call.
 *
 * The refresh middleware (see `refresh.ts`) catches the thrown `ApiError`,
 * detects 401, and performs single-flight refresh + retry.
 *
 * Correlation ID falls back to empty string if the backend didn't emit
 * `X-Correlation-Id` (shouldn't happen in production — backend Phase 1 emits
 * it on every response including errors).
 */
export const errorMiddleware: Middleware = {
  async onResponse({ response }) {
    if (response.ok) return undefined;

    const headerCorrelationId =
      response.headers.get("X-Correlation-Id") ?? response.headers.get("x-correlation-id") ?? "";

    let payload: Partial<ApiErrorPayload> = {};
    try {
      const ct = response.headers.get("content-type") ?? "";
      if (ct.includes("application/problem+json") || ct.includes("application/json")) {
        payload = (await response.clone().json()) as Partial<ApiErrorPayload>;
      }
    } catch {
      /* Body wasn't JSON — fall through to generic envelope below. */
    }

    // Prefer the response header, but fall back to the RFC 7807 body's
    // correlationId when the header is absent — otherwise support loses the id.
    const correlationId =
      headerCorrelationId.length > 0 ? headerCorrelationId : (payload.correlationId ?? "");

    const apiErrorPayload: ApiErrorPayload = {
      errorCode: (payload.errorCode ?? "UNKNOWN_ERROR") as ErrorCode,
      title: payload.title ?? `Request failed (${response.status})`,
      status: response.status,
      correlationId,
    };
    if (payload.detail !== undefined) apiErrorPayload.detail = payload.detail;
    if (payload.instance !== undefined) apiErrorPayload.instance = payload.instance;
    if (payload.messageKey !== undefined) apiErrorPayload.messageKey = payload.messageKey;

    // Preserve non-standard RFC 7807 members (extensions) so endpoint-specific
    // signals survive onto the thrown ApiError. The registered problem fields
    // (type/title/status/detail/instance) and the members we already lift onto
    // dedicated properties (errorCode/correlationId/messageKey) are excluded;
    // everything else — e.g. the 429 REPORT_QUOTA_EXHAUSTED variant's
    // `retryAfter` + `currentJobId` — lands in `extensions` verbatim.
    const COPIED_MEMBERS = new Set([
      "type",
      "title",
      "status",
      "detail",
      "instance",
      "errorCode",
      "correlationId",
      "messageKey",
    ]);
    const extensions: Record<string, unknown> = {};
    for (const [key, value] of Object.entries(payload as Record<string, unknown>)) {
      if (!COPIED_MEMBERS.has(key)) extensions[key] = value;
    }
    // A throttled response's `Retry-After` (delta-seconds) is otherwise lost with
    // the Response; carry it so a form can say how long to wait.
    const retryAfter = Number(response.headers.get("Retry-After") ?? "");
    if (response.status === 429 && Number.isFinite(retryAfter) && retryAfter > 0) {
      extensions.retryAfterSeconds = retryAfter;
    }
    if (Object.keys(extensions).length > 0) apiErrorPayload.extensions = extensions;

    throw new ApiError(apiErrorPayload);
  },
};
